// Public quest board. No auth required to view. For "I have 5
// minutes, what can I do?" use.
//
// If the visitor is signed in, the "Claim" button claims the quest
// directly (POST /api/me/quests/:id/claim) and routes to /player. If
// not, it routes to /login with a `?next=/quests/<id>` so they
// return here after signing in and can claim from this page.
//
// By default the board shows open quests across ALL teams. A team
// selector at the top lets the wall TV (or anyone) filter to a
// single team — e.g. a QA Team TV in the QA room only shows QA
// quests, not Dev or Platform. The selector is a team picker; the
// selected team_id is passed as a query param to /api/public/quests.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import { questsApi } from './api';
import type { PublicQuest } from './types';

export function PublicQuestBoardPage() {
  const me = useQuery({
    queryKey: ['me-public-quests'],
    queryFn: () => api<{ user: { id: string; display_name: string; role: string } }>('/api/auth/me'),
    retry: false,
    // Don't re-run this on focus; the AppShell already has the same
    // query and the value is stable across the session. This keeps
    // /quests light when navigated to as a public URL.
    staleTime: 60_000,
  });
  // Team list for the selector. Use the user's own teams (so the
  // selector doesn't show teams the user can't claim in). For
  // anonymous visitors the selector is hidden and the board shows
  // everything (or filtered by ?team_id=... in the URL).
  const teams = useQuery({
    queryKey: ['me-teams'],
    queryFn: () => api<{ teams: Array<{ id: string; name: string }> }>('/api/me/teams'),
    enabled: !!me.data?.user,
    staleTime: 60_000,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  // Resolve the effective team for this view.
  //
  // Signed-in user: always filter to one of the user's teams. If
  // the URL has ?team_id=... that matches one of their teams, use
  // it; otherwise default to their first team. The "All teams"
  // view is intentionally not exposed in the UI — the quest board
  // is a team-scoped view (a QA Team TV shows QA quests, not
  // every team's), and the org-wide public board is the
  // /api/public-board endpoint, not this one.
  //
  // Anonymous user (no session): show all teams, since the URL is
  // the only way to scope. Wall TVs embed this page with
  // ?team_id=... to filter to the relevant team.
  const userTeams = teams.data?.teams ?? [];
  const defaultTeam = userTeams[0]?.id ?? '';
  const teamParam = useMemo(() => {
    if (!me.data?.user) return searchParams.get('team_id') ?? '';
    const fromUrl = searchParams.get('team_id') ?? '';
    if (fromUrl && userTeams.some((t) => t.id === fromUrl)) return fromUrl;
    return defaultTeam;
  }, [me.data?.user, searchParams, userTeams, defaultTeam]);
  // If the URL has a team_id but it's not in the user's teams (or
  // the user is signed in and has no team in the URL), rewrite the
  // URL to the resolved value. Keeps the URL canonical.
  useEffect(() => {
    if (!me.data?.user) return;
    const fromUrl = searchParams.get('team_id') ?? '';
    if (fromUrl !== teamParam) {
      const next = new URLSearchParams(searchParams);
      if (teamParam) next.set('team_id', teamParam);
      else next.delete('team_id');
      setSearchParams(next, { replace: true });
    }
  }, [me.data?.user, teamParam, searchParams, setSearchParams]);
  const quests = useQuery({
    queryKey: ['public-quests', teamParam],
    queryFn: () => questsApi.publicBoard(teamParam || undefined),
  });

  const signedIn = !!me.data?.user;
  const list = quests.data?.quests ?? [];
  // After the defaultTeam resolution above, the selector only
  // renders for signed-in users with at least one team — for them
  // we always have a teamParam.
  const showSelector = signedIn && userTeams.length > 0;

  return (
    <div className="min-h-screen bg-bg px-4 py-6 text-ink">
      <div className="mx-auto max-w-5xl space-y-6">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">Quest board</h1>
            <p className="text-ink-muted">
              {signedIn
                ? 'Open quests anyone on the team can claim. Tap one to pick it up.'
                : 'Open quests anyone on the team can claim. Sign in to pick one up.'}
            </p>
          </div>
          {showSelector ? (
            <div className="flex items-center gap-2">
              <label htmlFor="team-filter" className="text-xs text-ink-muted">
                Team
              </label>
              <select
                id="team-filter"
                className="rounded-md border border-border bg-bg-elevated px-2 py-1 text-sm"
                value={teamParam}
                onChange={(e) => {
                  const next = new URLSearchParams(searchParams);
                  if (e.target.value) next.set('team_id', e.target.value);
                  else next.delete('team_id');
                  setSearchParams(next, { replace: true });
                }}
              >
                {userTeams.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          ) : null}
        </header>

        {quests.isLoading ? (
          <p className="text-ink-muted">Loading…</p>
        ) : list.length === 0 ? (
          <div className="card text-center">
            <p className="text-ink-muted">
              {teamParam
                ? 'No open quests in this team right now.'
                : 'No open quests right now. Come back later.'}
            </p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {list.map((q: PublicQuest) => (
              <QuestTile key={q.id} quest={q} signedIn={signedIn} />
            ))}
          </div>
        )}

        <footer className="pt-4 text-center text-sm text-ink-muted">
          {signedIn ? (
            <>
              Signed in as {me.data?.user?.display_name}. <Link to="/player" className="underline">My dashboard</Link>
            </>
          ) : (
            <>
              <Link to="/login?next=/quests" className="underline">Sign in</Link> · <Link to="/login" className="underline">My dashboard</Link>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}

function QuestTile({ quest, signedIn }: { quest: PublicQuest; signedIn: boolean }) {
  const filled = quest.claim_count >= quest.max_completions;
  const [err, setErr] = useState<string | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const claim = useMutation({
    mutationFn: () => questsApi.claim(quest.id),
    onSuccess: () => {
      // After a successful claim, route to the player dashboard so
      // the player can see the claim in context with the rest of
      // their active quests. Refetch the public board in case they
      // hit the back button.
      qc.invalidateQueries({ queryKey: ['public-quests'] });
      qc.invalidateQueries({ queryKey: ['me-quests'] });
      navigate('/player');
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 401) {
        // Session expired between page load and click. Send to login
        // with a return path so they come back and try again.
        navigate(`/login?next=/quests`);
      } else {
        setErr(e instanceof ApiError ? e.message : 'Claim failed.');
      }
    },
  });

  if (filled) {
    return (
      <div className="card flex flex-col gap-2 opacity-60">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-lg font-semibold">{quest.title}</h2>
          {/* M5.15: total XP (sum of splits). Multi-split
              quests show a small "N competencies" chip
              next to the headline. Coins (when >0)
              render as a separate "+N 🪙" badge in the
              coin-chip colour so the public board
              advertises the reward shape. */}
          <span className="flex items-baseline gap-2 font-mono text-sm">
            <span className="text-accent-success">+{quest.splits.reduce((s, x) => s + x.xp, 0)} XP</span>
            {quest.splits.reduce((s, x) => s + (x.coins ?? 0), 0) > 0 ? (
              <span className="text-accent-warning">+{quest.splits.reduce((s, x) => s + (x.coins ?? 0), 0)} 🪙</span>
            ) : null}
            {quest.splits.length > 1 ? (
              <span className="rounded-full bg-bg-elevated px-2 py-0.5 font-sans text-xs text-ink-muted">
                {quest.splits.length} competencies
              </span>
            ) : null}
          </span>
        </div>
        <p className="text-xs text-ink-dim">
          {quest.team_name}
          {quest.due_at ? ` · due ${new Date(quest.due_at).toLocaleDateString()}` : ''}
        </p>
        {quest.description ? <p className="text-sm text-ink-muted">{quest.description}</p> : null}
        <div className="mt-auto flex items-center justify-end pt-2">
          <p className="text-xs text-ink-dim">All slots filled</p>
        </div>
      </div>
    );
  }

  // Open quest. The button behaves differently based on auth state.
  const button = signedIn ? (
    <button
      className="btn-primary"
      onClick={() => {
        setErr(null);
        claim.mutate();
      }}
      disabled={claim.isPending}
    >
      {claim.isPending ? 'Claiming…' : 'Claim →'}
    </button>
  ) : (
    <Link to={`/login?next=/quests`} className="btn-primary">
      Claim →
    </Link>
  );

  return (
    <div className="card flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold">{quest.title}</h2>
        {/* M5.15: same XP / coins / competencies chip as
            the filled branch above. */}
        <span className="flex items-baseline gap-2 font-mono text-sm">
          <span className="text-accent-success">+{quest.splits.reduce((s, x) => s + x.xp, 0)} XP</span>
          {quest.splits.reduce((s, x) => s + (x.coins ?? 0), 0) > 0 ? (
            <span className="text-accent-warning">+{quest.splits.reduce((s, x) => s + (x.coins ?? 0), 0)} 🪙</span>
          ) : null}
          {quest.splits.length > 1 ? (
            <span className="rounded-full bg-bg-elevated px-2 py-0.5 font-sans text-xs text-ink-muted">
              {quest.splits.length} competencies
            </span>
          ) : null}
        </span>
      </div>
      <p className="text-xs text-ink-dim">
        {quest.team_name}
        {quest.due_at ? ` · due ${new Date(quest.due_at).toLocaleDateString()}` : ''}
        {/* M5.10: surface the repeatable flag on the
            public board too. Players browsing the
            public board need to know which quests they
            can do multiple times. */}
        {quest.repeatable ? ' · repeatable' : ''}
      </p>
      {quest.description ? <p className="text-sm text-ink-muted">{quest.description}</p> : null}
      {err ? <p className="text-sm text-accent-danger" role="alert">{err}</p> : null}
      <div className="mt-auto flex items-center justify-between pt-2">
        <p className="text-xs text-ink-dim">
          {quest.claim_count}/{quest.max_completions} claimed
        </p>
        {button}
      </div>
    </div>
  );
}
